import _ from 'lodash'
import * as d3color from 'd3-color'
import * as d3arr from 'd3-array'
import * as d3scale from 'd3-scale'
import {
	GREEN_DARK,
	GREEN_MEDIUM,
	ORANGE_MEDIUM, RED_DARK,
	RED_MEDIUM,
	YELLOW_MEDIUM
} from '../colors'

const RED = 'red'
const ORANGE = 'orange'
const YELLOW = 'yellow'
const GREEN = 'green'
const TURQUOISE = 'turquoise'
const BLUE = 'blue'
const DARKBLUE = 'darkblue'
const VIOLET = 'violet'
const PINK = 'pink'
const GRAY = 'gray'
const YELLOW_GREEN = 'yellow-green'
const YELLOW_BLUE = 'yellow-blue'
const YELLOW_ORANGE_RED = 'yellow-orange-red'
const YELLOW_RED_VIOLET = 'yellow-red-violet'
const ORANGE_WHITE_PURPLE = 'orange-white-purple'
const PINK_WHITE_GREEN = 'pink-white-green'
const RED_WHITE_BLUE = 'red-white-blue'
const RED_WHITE_GRAY = 'red-white-gray'
const RED_YELLOW_BLUE = 'red-yellow-blue'
const SCORING = 'scoring'

/**
 * The predefined gradients as described
 * https://github.com/SpiderStrategies/Scoreboard/issues/20258#issuecomment-1118694046
 */
const BASE_GRADIENTS = [
	{id: RED, colors: ['#FFE7E6', '#F25131', '#8A0900']},
	{id: ORANGE, colors: ['#FFF4E6', '#FF8800', '#8A0000']},
	{id: YELLOW, colors: ['#FFFFE6', '#FBCC3B', '#8A3300']},
	{id: GREEN, colors: ['#F3FFCC', '#96CD00', '#0B6600']},
	{id: TURQUOISE, colors: ['#E9FEFF', '#60CBDB', '#0F4F6B']},
	{id: BLUE, colors: ['#E6F7FF', '#1A94DA', '#002575']},
	{id: DARKBLUE, colors: ['#ECF1FF', '#839AFF', '#1C2F69']},
	{id: VIOLET, colors: ['#F7E7FF', '#AD5AFA', '#500575']},
	{id: PINK, colors: ['#FFE7FF', '#E55BFA', '#700570']},
	{id: GRAY, colors: ['#F8F9FA', '#20313D'], thresholds: [0,1]},
	{id: YELLOW_GREEN, colors: ['#FFFFD9', '#AACC00', '#0D7500']},
	{id: YELLOW_BLUE, colors: ['#FFFFD9', '#60CBDB', '#0F4F6B']},
	{id: YELLOW_ORANGE_RED, colors: ['#FFFFD9', '#FBCC3B', '#FF8800', '#F25131', '#8A0900'], thresholds: [0, .3, .5, .7, 1]},
	{id: YELLOW_RED_VIOLET, colors: ['#FFFFD9', '#FBCC3B', '#C93C20', '#832D74', '#641D91', '#500575'], thresholds: [0, .23, .5, .68, .87, 1]},
	{id: ORANGE_WHITE_PURPLE, colors: ['#8A0000', '#FF8800', '#FFFFFF', '#AD5AFA', '#500575'], thresholds: [0, .3, .5, .7, 1]},
	{id: PINK_WHITE_GREEN, colors: ['#700570', '#E55BFA', '#FFFFFF', '#96CD00', '#0D7500'], thresholds: [0, .3, .5, .7, 1]},
	{id: RED_WHITE_BLUE, colors: ['#8A0900', '#F25131', '#FFFFFF', '#1A94DA', '#002575'], thresholds: [0, .3, .5, .7, 1]},
	{id: RED_WHITE_GRAY, colors: ['#8A0900', '#F25131', '#FFFFFF', '#20313D'], thresholds: [0, .3, .5, 1]},
	{id: RED_YELLOW_BLUE, colors: ['#8A0900', '#F25131', '#FF8800', '#FFE27C', '#FFFFD9', '#60CBDB', '#002575'], thresholds: [0, .2, .3, .4, .5, .7, 1]},
	{id: SCORING, colors: ['#8A0900', '#F25131', '#FF8800', '#FBCC3B', '#96CD00', '#0D7500'], thresholds: [0, .2, .33, .5, .7, 1]}
]

const gradientsById = {}
const continuousGradientDataById = {}

/**
 * Takes a number or an array of thresholds and returns an array of numbers
 * from [0,1] indicating the percentage where each threshold lies.  If a number
 * is passed in, each threshold is calculated in equal portions for example,
 * a 5 threshold quantile would return [0, .25, .5, .75, 1]
 */
function toQuantiles(thresholdsOrNumberOfThresholds) {
	let quantiles = [0,1]
	let numQuantiles, determineQuantile
	if (_.isArray(thresholdsOrNumberOfThresholds)) {
		numQuantiles = thresholdsOrNumberOfThresholds.length-1
		let size = thresholdsOrNumberOfThresholds[numQuantiles]-thresholdsOrNumberOfThresholds[0]
		determineQuantile = (i) => {
			//divide the quantiles into length proportional to the size of the
			//thresholds (e.g. 0,2,7,10) -> (0%, 20%, 70%, 100%)
			return (thresholdsOrNumberOfThresholds[i] - thresholdsOrNumberOfThresholds[0]) / size
		}
	} else {
		numQuantiles = thresholdsOrNumberOfThresholds-1
		determineQuantile = (i) => {
			//divide the number of colors into equal quantiles
			return (1/numQuantiles) * i
		}
	}
	for (let i=1;i<numQuantiles;i++) {
		let q = determineQuantile(i)
		quantiles.splice(d3arr.bisectLeft(quantiles, q), 0, q)
	}
	return quantiles
}

function valueToContinuousColor(colorScale, value, domain, opts = {}) {
	let domainScale = d3scale.scaleLinear().domain(domain).range([0,1])
	let defaultColor = opts.defaultColor || d3color.color('white')
	return _.isNil(value) ? defaultColor : d3color.color(colorScale(domainScale(value)))
}

/**
 * A data class containing the intervals, colorsScale , and valueToColor function
 * that takes a value and return the corresponding color
 * @param intervals The intervals which contain the colors paired with their threshold position
 * @param colors The colors in order of the gradient
 * @param colorBoundaries The boundaries where the color changes from one the next (expressed
 * as a decimal between 0 and 1)
 * NOTE: this is not to be confused with user defined thresholds that are used
 * in conditional coloring
 * @param continuous true if this data representing continuous color scale
 *
 * @return {Object} data.colorScale The D3 Scale the representing this gradient
 * @return {Function} data.valueToColor A function that takes a value and thresholds
 * and returns the corresponding color that value represents against this gradient
 */
class GradientData {
	constructor(intervals, colors, colorBoundaries, continuous, thresholds) {
		this.continuous = continuous
		this.intervals = intervals
		this.colorScale = d3scale.scaleLinear().domain(colorBoundaries).range(colors).clamp(true)
		this.colors = colors
		this.thresholds = thresholds
	}

	/**
	 * Given a value, returns the corresponding d3.color in the gradient that value
	 * is associated with.
	 *
	 * @param {Number} value a numerical value
	 * @return {Rgb} The D3.color object that represents that value in the gradient
	 */
	valueToColor(value, opts = {}) {
		let thresholds = this.thresholds || [0, 100]
		let color
		if (this.continuous) {
			let domain = [thresholds[0], thresholds[thresholds.length-1]]
			color = valueToContinuousColor(this.colorScale, value, domain, opts)
		} else {
			let colors = this.colors
			if (thresholds[0] > thresholds[thresholds.length-1]) {
				thresholds = _.sortBy(thresholds)
				colors = colors.reverse()
			}
			//bisectRight for ties https://github.com/SpiderStrategies/Scoreboard/issues/20258#issuecomment-1115120330
			let index = 0
			if (thresholds[thresholds.length-1] <= value) {
				index = colors.length-1
			} else if (thresholds[0] < value) {
				index = Math.min(d3arr.bisectRight(thresholds, value)-1, colors.length-1)
			}
			color = d3color.color(colors[index])
		}
		if (_.isFinite(opts.opacity)) {
			color = color.copy({opacity: opts.opacity})
		}
		return color
	}
}

/**
 * Generate an object containing data used to build to color against a continuous
 * gradient using the passed in the number of segments
 *
 * @param {Array} colors array of color values in order they appear in the gradient
 * @param {Array} colorBoundaries array of positions (as a decimal between 0 and
 * 1) indicating that color's boundary in the gradient
 * @param {Boolean} reverse true if the gradient should be built in reverse order
 * to its definition
 * @return {GradientData} data The discrete data object containing the intervals,
 * colors, and valueToColor function that takes a value and return the corresponding
 * color
 */
function continuousData(colors, colorBoundaries, reverse) {
	let intervals = []
	if (_.isNil(colorBoundaries)) {
		colorBoundaries = toQuantiles(colors.length)
	}

	if (reverse) {
		colors = [].concat(colors).reverse()
		colorBoundaries = _.map(colorBoundaries, (t) => { return 1-t }).reverse()
	}

	_.map(colors, (c, i) => {
		let color = d3color.color(c)
		let threshold = colorBoundaries[i]
		intervals.push({ color, threshold })
	})

	let data = new GradientData(intervals, colors, colorBoundaries, true)
	data.cssLinearGradient = _(intervals).map((data) => {
		return `${data.color} ${data.threshold*100}%`
	}).join(', ')
	return data
}

/**
 * Generate an object containing data used to build to color against a discrete
 * gradient using the passed in the number of segments
 * @param {String} gradientId The id of the gradient
 * @param {Object} continuousColorScale The continuous color scale to base this discrete
 * scale off of
 * @param {Array | Integer} thresholdsOrNumSegments The number of segments in the
 * discrete gradient or a list of thresholds associated with the gradient
 * @param {Boolean} reversed true if the gradient should be built in reverse order
 * to its definition
 * @return {GradientData} data The discrete data object containing the intervals,
 * colors, and valueToColor function that takes a value and return the corresponding
 * color
 */
function discreteData(gradientId, continuousColorScale, thresholdsOrNumSegments, reversed) {
	let intervals = []
	let gradientBoundary = []
	let colors = []
	let thresholds = null
	let numSegments = thresholdsOrNumSegments
	if (_.isArray(thresholdsOrNumSegments)) {
		thresholds = thresholdsOrNumSegments
		numSegments = thresholdsOrNumSegments.length - 1
	}

	let segmentSize = 1/numSegments
	let quantiles = toQuantiles(numSegments)
	_.each(quantiles, (boundary, i) => {
		let color = d3color.color(continuousColorScale(boundary))
		let start = segmentSize * i
		let end = segmentSize * (i+1)
		intervals.push({ color, threshold: boundary, start, end })
		colors.push(color)
		gradientBoundary.push(boundary)
	})

	// #59308 we have fixed colors we want to use for scoring gradients
	if (gradientId == SCORING) {
		let hexColors = getScoringColors(numSegments)
		if (hexColors) {
			colors = _.map(hexColors, (c) => d3color.rgb(c))
			if (reversed) {
				colors.reverse()
			}
		}
		_.each(intervals, (interval, i) => {
			interval.color = colors[i]
		})
	}

	return new GradientData(intervals, colors, gradientBoundary, false,
		thresholds)
}

/**
 * Returns the fixed colors to be used for scoring gradients, if applicable. If
 * there aren't fixed colors for the given number of segments, returns null.
 * @param numSegments The number of segments in the gradient
 * @return {(string)[]} The fixed colors to be used for scoring gradients
 */
function getScoringColors(numSegments) {
	switch (numSegments) {
		case 2: return [RED_MEDIUM, GREEN_MEDIUM]
		case 3: return [RED_MEDIUM, YELLOW_MEDIUM, GREEN_MEDIUM]
		case 4: return [RED_MEDIUM, ORANGE_MEDIUM, YELLOW_MEDIUM, GREEN_MEDIUM]
		case 5: return [RED_DARK, RED_MEDIUM, YELLOW_MEDIUM, GREEN_MEDIUM, GREEN_DARK]
	}
}

/**
 * Given an array of colors and an optional array of intervals where the colors
 * are positioned in the gradient, generates the continuous that can be used to compose
 * discrete/continuous gradients as well as a function to interpret the color
 * of an arbitrary value against a user defined range.
 *
 * @param {Array} colors The array of colors from first to last in the gradient.
 * The colors can be in any well known color format (e.g rgb, hsl, etc...)
 * @param {Array} [thresholds] the thresholds where each color should be positioned
 * within the gradient.  The array must have the same number of colors if specified
 * and each interval is interpreted between a decimal from 0 to 1.  If the interval
 * is not given it is assumed each color is to be positioned into equal sizes.
 *
 * @return {Object} data The continuous gradient data
 * @return {Object} data.normal the gradient in order as it is defined
 * @return {Object} data.reversed the gradient in reverse order as it is defined
 */
function generateContinuousGradientData(colors, thresholds) {
	return {
		normal: continuousData(colors, thresholds),
		reversed: continuousData(colors, thresholds, true)
	}
}

/**
 * Returns an object that defines methods to return either continuous or discrete
 * gradient data.
 *
 * @return {Function} continuous This function takes a boolean "reversed" and
 * returns a "GradientData" object caller can use to construct continuous gradients
 * or find the color of an arbitrary value associated with the thresholds defined
 * in the gradient
 *
 * @return {Function} discrete This function takes a boolean "reversed" and
 * either an integer representing the number of segments or an array of thresholds
 * associated with the gradient and returns a "GradientData" object caller can
 * use to construct discrete (segmented) gradients. If the number is passed in
 * the segments in the gradient are calculated in equal proportion.  "GradientData"
 * will also contain a function to return the segment color associated with
 * an arbitrary value against the defined thresholds.  If no thresholds were
 * defined, it uses a predefined [0, 100] threshold
 *
 */
function generateGradientData(gradientId) {
	let data = continuousGradientDataById[gradientId]
	return {
		continuous: function(reversed = false) {
			return reversed ? data.reversed : data.normal
		},
		discrete: function(segmentsOrThresholds, reversed = false) {
			return discreteData(gradientId, this.continuous(reversed).colorScale, segmentsOrThresholds, reversed)
		}
	}
}

//for each base gradient defined generate the data used to create continuous and
//discrete gradients
_.each(BASE_GRADIENTS, g => {
	continuousGradientDataById[g.id] = generateContinuousGradientData(g.colors, g.thresholds)
	gradientsById[g.id] = generateGradientData(g.id)
})

export default {

	/**
	 * Returns all the gradients ids
	 * @return {string[]}
	 */
	ids() {
		return _.keys(gradientsById)
	},

	/**
	 * Return the gradient data associated with this id or null if nothing found
	 * @param gradientId
	 * @return {null|*}
	 */
	byId(gradientId) {
		let gradient = gradientsById[gradientId]
		if (gradient) {
			return _.cloneDeep(gradient)
		}
		return null
	},

	all() {
		return _.cloneDeep(gradientsById)
	}

}
export {
	RED,
	ORANGE,
	YELLOW,
	GREEN,
	TURQUOISE,
	BLUE,
	DARKBLUE,
	VIOLET,
	PINK,
	GRAY,
	YELLOW_GREEN,
	YELLOW_BLUE,
	YELLOW_ORANGE_RED,
	YELLOW_RED_VIOLET,
	ORANGE_WHITE_PURPLE,
	PINK_WHITE_GREEN,
	RED_WHITE_BLUE,
	RED_WHITE_GRAY,
	RED_YELLOW_BLUE,
	SCORING
}